import $ from 'jquery'
import { showModal } from '~/common/helpers/modal'
import scrollIntoView from 'scroll-into-view-if-needed'
import { replaceWithPreserves } from '../helpers/replace'
import { morphReplace } from '../helpers/morph_replace'
import { getIframeInParentDoc } from '../helpers/iframe'
import { highlight } from '../helpers/highlight'
import { fade } from '../helpers/fade'
import { entrypoints } from '../helpers/entrypoints'
import { handleClientSideSubmitResult } from '~/common/components/stripe'
import { ajaxErrorArgs } from '../helpers/ajax_error_args'
import { suppressUnloadWarning } from '~/common/helpers/unload_warning'

$.extend($.ajaxSettings, {
  async: true,
  dataType: 'json',
  type: 'POST',
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
  },
})
// X-Requested-With doesn't get set automatically for cross-domain requests for some reason
// Don't know who's normally responsible for setting it, but this works
// http://stackoverflow.com/questions/23610014/request-xhr-cors-layout-fail

// jquery-ujs serializes forms with serializeArray(), which cannot carry a file. So on
// submit it fires ajax:aborted:file and — unless a handler returns false — lets the
// browser submit the form natively instead. For a remote form that is silently broken:
// remote forms embed no authenticity_token (it normally rides on the XHR header), so the
// native POST arrives without one and Rails answers 422 InvalidAuthenticityToken. Send it
// as multipart FormData ourselves instead. Returning false is what stops jquery-ujs from
// falling back to the native submit.
//
// Events go out as native CustomEvents carrying rails-ujs's [data, status, xhr] detail:
// jQuery's delegated handlers (ajax-update below, jquery-ujs's own form re-enabling) see
// native events, but the reverse isn't true — jQuery's trigger() never reaches an
// addEventListener handler, and the chat composer listens natively.
$(document).on(
  'ajax:aborted:file',
  '[data-behavior~=ajax-update]',
  function () {
    const form = this
    const fire = (name, detail) =>
      form.dispatchEvent(
        new CustomEvent(name, { detail, bubbles: true, cancelable: true })
      )

    if (!fire('ajax:before', [form])) return false

    // The CSRF token is added by jquery-ujs's own $.ajaxPrefilter for same-origin calls.
    $.ajax({
      url: form.getAttribute('action'),
      type: (form.getAttribute('method') || 'POST').toUpperCase(),
      data: new FormData(form),
      dataType: $(form).data('type') || undefined,
      processData: false,
      contentType: false,
      beforeSend: (xhr, settings) => fire('ajax:send', [xhr, settings]),
      success: (data, status, xhr) => fire('ajax:success', [data, status, xhr]),
      error: (xhr, status, error) => fire('ajax:error', [xhr, status, error]),
      complete: (xhr, status) => fire('ajax:complete', [xhr, status]),
    })

    return false
  }
)

$(document).on('ajax:success', '[data-update]', function (event, data) {
  const $this = $(this)
  const target = $this.data('update')
  const effect = $this.data('effect')
  $('#' + target)
    .html(data[target])
    .changed()
  if (effect) {
    $('#' + target).effect(effect, {}, 5000)
  }
})

$(document).on('ajax:success', '[data-replace]', function (event, data) {
  const $this = $(this)
  const target = $this.data('replace')
  const effect = $this.data('effect')
  $('#' + target).replaceWith(data[target])
  $('#' + target).changed()
  if (effect) {
    $('#' + target).effect(effect, {}, 5000)
  }
})

$(document).on('ajax:success', '[data-remove]', function () {
  const $this = $(this)
  const effect = $this.data('effect')
  const target = $('#' + $this.data('remove'))
  if (effect != null) {
    target.effect(effect, {}, 500, () => target.remove())
  } else {
    target.remove()
  }
})

const idify = function (selector) {
  if (
    selector[0] === '.' ||
    selector[0] === '#' ||
    selector[0] === '[' ||
    selector === 'head' ||
    selector === 'body'
  ) {
    return selector
  } else {
    return '#' + selector
  }
}

// Payload may be a plain HTML string or { html, css_entrypoints, js_entrypoints }.
// Loads any referenced Vite entrypoints, then returns the HTML to inject.
const prepareHtml = function (payload) {
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    if (payload.css_entrypoints || payload.js_entrypoints) {
      entrypoints(payload.css_entrypoints, payload.js_entrypoints)
    }
    return payload.html ?? payload
  }
  return payload
}

// Set a form field to a value, choosing the right mechanism for whatever editor
// backs it — TipTap, legacy TinyMCE, ProseMirror, or a plain input — and firing the
// change/input events so `submit-on-change` autosave picks it up. This is what lets
// the `set_field_values` directive fill fields from the server without re-rendering
// a partial. `el` is the field's input/textarea element.
//
// Each path sets the value in an *undoable* way, so Ctrl+Z restores the previous
// content (e.g. when an AI write overwrote something by mistake).
const setFieldValue = function (el, value) {
  if (!el) return

  const tiptap = el.closest && el.closest('.js-tiptap-editor-container')
  if (tiptap) {
    // `undoable` makes the editor replace-via-insert so the change lands on its history.
    tiptap.dispatchEvent(
      new CustomEvent('editor:setContent', {
        detail: {
          content: value,
          immediate: true,
          focus: 'end',
          undoable: true,
        },
      })
    )
    return
  }

  const tinymce = window.tinymce?.EditorManager?.editors?.[el.id]
  if (tinymce) {
    // transact() records a single undo level around the content replacement.
    tinymce.undoManager.transact(() => tinymce.setContent(value))
    tinymce.save()
    el.dispatchEvent(new Event('change', { bubbles: true }))
    return
  }

  const proseMirror = window.proseMirror?.editors?.[el.id]
  if (proseMirror?.setValueUndoable) {
    proseMirror.setValueUndoable(value)
    el.dispatchEvent(new Event('change', { bubbles: true }))
    return
  }

  // Plain input/textarea: insertText lands the change on the browser's native undo
  // stack (a direct `el.value =` assignment would not be undoable).
  el.focus()
  el.select?.()
  if (
    !(document.execCommand && document.execCommand('insertText', false, value))
  ) {
    el.value = value
  }
  el.dispatchEvent(new Event('input', { bubbles: true }))
  el.dispatchEvent(new Event('change', { bubbles: true }))
}

// Strip Trix editor inputs before navigating away — Safari otherwise shows an
// "unsaved changes" prompt until the user has clicked "Leave page" once.
const clearTrixBeforeUnload = () =>
  $('.trix-editor-container').find('input').remove()

const focusFirstInput = function (selector, { includeEditors = false } = {}) {
  const types = includeEditors
    ? 'input:visible, textarea:visible, [contenteditable]:visible'
    : 'input:visible, textarea:visible'
  const firstInput = selector
    .find(types)
    .filter(function () {
      return !$(this).closest('.js-object-picker-select').length
    })
    .first()

  if (
    firstInput.length > 0 &&
    firstInput.parents('.js-disable-ajax-autofocus').length == 0
  ) {
    firstInput.focus()
    try {
      firstInput[0].setSelectionRange(-1, -1)
    } catch (e) {
      // InvalidStateError: The input element's type ('radio') does not support selection.
      // There are more types of inputs and this works only on text.
    }
  }
}

// When a crud form (side panel / modal) re-renders its fields after a failed save, the error
// summary sits at the top of the freshly rendered fields. If the user submitted from far down
// a long form, the scroll container stays put and nothing visibly changes — so scroll the error
// summary back into view. `#crud_action_fields` is the shared container id used by
// HasCrudActionsConcerns and the crud_actions edit component; the summary id depends on the form
// builder (`tw-error-explanation` for Tailwind/boxy, `error_explanation` otherwise).
const scrollCrudErrorsIntoView = function (fields) {
  const errorSelector = '#tw-error-explanation, #error_explanation'
  const errorSummary = fields.find(errorSelector).addBack(errorSelector)[0]
  if (!errorSummary) return

  scrollIntoView(errorSummary, {
    block: 'start',
    scrollMode: 'if-needed',
    behavior: 'smooth',
  })
}

const each_selector = function (object, callback) {
  if (!object) return
  for (let selector in object) {
    const value = object[selector]
    callback.call($(idify(selector)), value)
  }
}

const showNoticeWithFireworks = function (data) {
  const notify = () => window.showNotice('notice', data.notice)
  if (data.celebrate != null) {
    window.Simplero.celebrate(data.celebrate, notify)
  } else {
    notify()
  }
}

// Coach embedded in admin is a same-origin frame: reload the whole page, since the login page
// won't load inside a frame. A cross-origin parent can't be reloaded from here.
function reloadTopmostPage() {
  try {
    if (window.top && window.top !== window) {
      window.top.location.reload()
      return
    }
  } catch {
    // cross-origin parent
  }
  window.location.reload()
}

export const AjaxHelper = {
  doUpdate(data, eventTarget) {
    $('[rel=popover]').popover?.('hide')

    // Remove first, so we don't remove something we've just inserted

    if (data.hide != null) {
      for (const id of Array.from(data.hide)) {
        $(idify(id)).hide()
      }
    }
    if (data.remove != null) {
      for (const id of Array.from(data.remove)) {
        $(idify(id)).remove()
      }
    }

    if (data.clear_local_storage) {
      for (let storage_key of Array.from(data.clear_local_storage)) {
        window.LocalDraftManager.clearLocalStorage(storage_key)
      }
    }

    if (data.hide_clickover) {
      $('[data-clickover-open=1]').each(function () {
        $(this).data('clickover') && $(this).data('clickover').clickery()
      })
    }

    if (data.fade != null) {
      for (const id of Array.from(data.fade)) {
        const $el = $(idify(id))
        if ($el.effect) {
          $el.effect('fade', {}, 500, function () {
            const parent = $(this).parent()
            $(this).remove()
            parent.changed()
          })
        } else {
          fade($el[0])
        }
      }
    }

    // Always hide these
    $('.tooltip,.clickover').remove()

    // Flag the window as performing AJAX updates.
    window.Simplero.state.isAjaxUpdating = true

    if (data.refresh_table != null) {
      for (const id of Array.from(data.refresh_table)) {
        $(idify(id)).data('simpleroManagerCustomView')?.refreshTable()
      }
    }

    if (data.refresh_calendar != null) {
      document.querySelectorAll('[data-controller*=calendar]').forEach((el) =>
        el.dispatchEvent(
          new CustomEvent('refresh-calendar', {
            bubbles: true,
            cancelable: true,
          })
        )
      )
    }

    if (data.update_calendar_events != null) {
      document.querySelectorAll('[data-controller*=calendar]').forEach((el) =>
        el.dispatchEvent(
          new CustomEvent('update-calendar-events', {
            bubbles: true,
            cancelable: true,
            detail: {
              events: data.update_calendar_events,
            },
          })
        )
      )
    }

    if (data.invalidate_queries != null) {
      for (let query of Array.from(data.invalidate_queries)) {
        // Need this on the webpack side as well, triggering via jquery would requires us to use jquery
        const event = new CustomEvent('simplero:invalidate-query', {
          bubbles: true,
          cancelable: true,
          detail: { query },
        })
        document.dispatchEvent(event)
      }
    }

    let html, elm, doc
    if (data.update != null) {
      for (const id in data.update) {
        html = prepareHtml(data.update[id])
        elm = $(idify(id))
        if (elm.is('iframe')) {
          doc = elm[0].contentWindow.document
          doc.open()
          doc.write(html)
          doc.close()
        } else {
          elm.html(html).changed()
        }
        if (data.focus_after_update) {
          // includeEditors so TipTap contenteditables (coach/chatbot message input) receive
          // focus the same way focus_after_replace does — plain input/textarea only misses them.
          focusFirstInput($(idify(id)), { includeEditors: true })
        }
        if (id === 'crud_action_fields') {
          scrollCrudErrorsIntoView(elm)
        }
      }
    }

    if (data.update_selector != null) {
      for (const selector in data.update_selector) {
        html = prepareHtml(data.update_selector[selector])
        elm = $(selector)
        if (elm.is('iframe')) {
          doc = elm[0].contentWindow.document
          doc.open()
          doc.write(html)
          doc.close()
        } else {
          elm.html(html).changed()
        }
      }
    }

    // Hash of id => html
    if (data.replace != null) {
      for (const id in data.replace) {
        html = prepareHtml(data.replace[id])

        const selector = idify(id)
        const newElement = replaceWithPreserves($(selector), html)
        if (data.focus_after_replace) {
          focusFirstInput(newElement, { includeEditors: true })
        }
        if (id === 'crud_action_fields') {
          scrollCrudErrorsIntoView(newElement)
        }
      }
    }

    // Hash of id => html — like `replace`, but uses Idiomorph to morph the
    // existing DOM into the new HTML instead of replacing it. Use this when
    // the target subtree contains iframes (Stripe Elements, embedded video)
    // or other state that would be destroyed by detach/reattach.
    if (data.morph_replace != null) {
      for (const id in data.morph_replace) {
        html = prepareHtml(data.morph_replace[id])
        const newElement = morphReplace($(idify(id)), html)
        if (data.focus_after_replace && newElement) {
          focusFirstInput(newElement, { includeEditors: true })
        }
      }
    }

    // Hash of id => html
    // Similar to replace, exxcept it will query the html for the selector and only replace it with that
    if (data.extract_and_replace != null) {
      for (const id in data.extract_and_replace) {
        const selector = idify(id)
        html = $(prepareHtml(data.extract_and_replace[id])).find(selector)
        $(selector).replaceWith(html)
        $(selector).changed()
      }
    }

    // Hash of id => html
    if (data.append != null) {
      for (const id in data.append) {
        html = prepareHtml(data.append[id])
        $(idify(id)).append(html)
        focusFirstInput($(idify(id)).changed())
      }
    }

    // Hash of id => html, appended — except for any node already on the page, which is left
    // exactly as it is. This is what lets a push and a catch-up asking for the same thing cross
    // in flight without drawing it twice: whoever arrives first wins and the other is a no-op.
    // Deliberately not a replace — what is on screen may hold state the server never knew about.
    //
    // Every top-level node must carry an id, because the id is the only thing that says whether
    // it is already there. Anything else is dropped rather than appended blind, which is also
    // what keeps the whitespace between elements from piling up over a long poll.
    if (data.append_if_missing != null) {
      for (const id in data.append_if_missing) {
        const container = $(idify(id))
        if (!container.length) continue

        const missing = (
          $.parseHTML(
            prepareHtml(data.append_if_missing[id]),
            document,
            true
          ) || []
        ).filter((node) => node.id && !document.getElementById(node.id))
        if (!missing.length) continue

        missing.forEach((node) => container.append(node))
        container.changed()
      }
    }

    // Hash of id => html, prepended — except for any node already on the page. The prepend twin of
    // append_if_missing: a push-driven refresh and the response that creates the node can land in
    // either order, and whichever comes second is a no-op. Every top-level node must carry an id.
    if (data.prepend_if_missing != null) {
      for (const id in data.prepend_if_missing) {
        const container = $(idify(id))
        if (!container.length) continue

        const missing = (
          $.parseHTML(
            prepareHtml(data.prepend_if_missing[id]),
            document,
            true
          ) || []
        ).filter((node) => node.id && !document.getElementById(node.id))
        if (!missing.length) continue

        container.prepend(missing)
        container.changed()
      }
    }

    // Hash of id => html
    if (data.prepend != null) {
      for (const id in data.prepend) {
        html = prepareHtml(data.prepend[id])
        $(idify(id)).prepend(html)
        focusFirstInput($(idify(id)).changed())
      }
    }

    // Hash of selector => value. Sets each field to the value, editor-aware (plain
    // input or TipTap/TinyMCE/ProseMirror wysiwyg). Lets the server fill form fields
    // without re-rendering them — see setFieldValue.
    if (data.set_field_values != null) {
      for (const selector in data.set_field_values) {
        setFieldValue(
          document.querySelector(idify(selector)),
          data.set_field_values[selector]
        )
      }
    }

    if (data.insert_after != null) {
      for (const id in data.insert_after) {
        html = prepareHtml(data.insert_after[id])
        const $el = $(idify(id)).after(html).next().changed()
        if (html) {
          focusFirstInput($el)
        }
      }
    }

    if (data.insert_before != null) {
      for (const id in data.insert_before) {
        html = prepareHtml(data.insert_before[id])
        focusFirstInput($(idify(id)).before(html).prev().changed())
      }
    }

    if (data.enable != null) {
      for (const selector of Array.from(data.enable)) {
        $(idify(selector)).removeClass('disabled').attr('disabled', false)
      }
    }

    if (data.fire_gtm_event != null && typeof fireGTMEvent !== 'undefined') {
      window.fireGTMEvent(data.fire_gtm_event)
    }

    if (data.fire_facebook_pixel != null) {
      const { event_name, event_id, custom, payload } = data.fire_facebook_pixel
      const track = () => {
        if (typeof window.fbq !== 'function') return
        window.fbq(
          custom ? 'trackCustom' : 'track',
          event_name,
          payload || {},
          { eventID: event_id }
        )
      }
      if (typeof window.simpleroCookieConsent === 'undefined') {
        track()
      } else {
        window.simpleroCookieConsent.whenCookiesAllowed(track)
      }
    }

    each_selector(data.remove_class, function (klass) {
      this.removeClass(klass)
    })
    each_selector(data.add_class, function (klass) {
      this.addClass(klass)
    })

    each_selector(data.update_attribute, function (updates) {
      for (let attribute in updates) {
        this.attr(attribute, updates[attribute])
      }
    })

    each_selector(data.clear_inline_style, function (styleKey) {
      this.css(styleKey, '')
    })

    if (data.error != null && data.error.replace(/^\s+|\s+$/g, '') !== '') {
      alert(data.error)
    }

    if (data.focus_selector != null) {
      $(data.focus_selector).focus()
    }

    if (data.errors_on != null) {
      for (const id in data.errors_on) {
        const message = data.errors_on[id]
        const input = $(idify(id))
        // Boxy/Tailwind fields have no .form-group. They render an (initially hidden) error slot
        // when asked to via always_render_skeleton, so we only fill or clear its text here.
        const twField = input
          .closest('.form-group, .t-field')
          .filter('.t-field')
        if (twField.length) {
          const errorSlot = twField.find('.error-message').first()
          if (!errorSlot.length) {
            if (message) alert(message)
            continue
          }
          errorSlot.html(message || '')
          const helpBlock = errorSlot.closest('.help-block')
          helpBlock.toggleClass('hidden', helpBlock.text().trim() === '')
          continue
        }
        const formgroup = input.closest('.form-group')
        formgroup
          .removeClass('has-error')
          .find('.form-control-feedback, .help-block')
          .remove()
        if (message != null && message !== '') {
          formgroup
            .addClass('has-error')
            .append($('<p></p>').addClass('help-block').html(message))
        }
      }
    }

    if (data.editor_insert) {
      window.tinyMCE.activeEditor?.insertContent(data.editor_insert)
      window.prosemirror_editor_insert_callback?.(data.editor_insert)
    }

    if (data.saved != null) {
      $('form.dirty').removeClass('dirty')
    }

    if (data.copy_to_clipboard != null) {
      navigator.clipboard.writeText(data.copy_to_clipboard)
    }

    if (data.open_url != null) {
      window.open(data.open_url, '_blank')
    }

    if (data.changed != null) {
      for (const id of Array.from(data.changed)) {
        $('#' + id).changed()
      }
    }

    if (data.side_panel != null) {
      let detail = data.side_panel
      if (typeof detail !== 'object') {
        detail = { content: data.side_panel }
      }

      document
        .getElementById('side_panel')
        .dispatchEvent(new CustomEvent('show', { detail }))
    }

    if (data.hide_side_panel != null) {
      // Must route through side-panel#hide (Stimulus action on #side_panel) so TipTap
      // BubbleMenus are force-dismissed before the panel closes (ENG-31181). Do not
      // remove .open here — hide() owns that and the toolbar cleanup.
      document
        .getElementById('side_panel')
        ?.dispatchEvent(new CustomEvent('hide', { cancelable: true }))
    }

    if (data.pop_to_root_pane) {
      const target =
        typeof data.pop_to_root_pane == 'string'
          ? document.querySelector(idify(data.pop_to_root_pane))
          : eventTarget
      target.dispatchEvent(
        new CustomEvent('panes:popToRoot', { bubbles: true })
      )
    }

    if (data.push_pane != null) {
      let content = data.push_pane.content || data.push_pane
      let target =
        !!data.push_pane.selector &&
        document.querySelector(idify(data.push_pane.selector))
      target = target || $(eventTarget).data('portal') || eventTarget
      let removeMiddlePanes = data.push_pane.removeMiddlePanes || false

      target.dispatchEvent(
        new CustomEvent('panes:push', {
          detail: { content, removeMiddlePanes },
          bubbles: true,
        })
      )
    }
    if (data.replace_pane != null) {
      let target =
        !!data.replace_pane.selector &&
        document.querySelector(idify(data.replace_pane.selector))
      target = target || $(eventTarget).data('portal') || eventTarget

      const detail =
        typeof data.replace_pane == 'string'
          ? { content: data.replace_pane }
          : data.replace_pane

      target.dispatchEvent(
        new CustomEvent('panes:replace', {
          detail,
          bubbles: true,
        })
      )
    }
    // Dispatch the pick BEFORE popping the pane: the pick needs the (still-attached) create
    // form to find its object-picker--mini ancestor; pop_pane would detach it first.
    if (data.object_picker_pick) {
      const target = $(eventTarget).data('portal') || eventTarget
      target.dispatchEvent(
        new CustomEvent('object-picker:pickFromJSEvent', {
          detail: data.object_picker_pick,
          bubbles: true,
        })
      )
    }
    if (data.pop_pane != null) {
      let target =
        !!data.pop_pane.selector &&
        document.querySelector(idify(data.pop_pane.selector))
      target = target || $(eventTarget).data('portal') || eventTarget
      target?.dispatchEvent(new CustomEvent('panes:pop', { bubbles: true }))
    }
    if (data.set_panes != null) {
      const target = $(eventTarget).data('portal') || eventTarget
      target.dispatchEvent(
        new CustomEvent('panes:set', {
          detail: { content: data.set_panes },
          bubbles: true,
        })
      )
    }

    if (data.re_render) {
      const targets = new Set()
      data.re_render.forEach((key) => {
        document
          .querySelectorAll(`[data-re-render-keys*="${key}"]`)
          .forEach((el) => targets.add(el))
      })
      targets.forEach((target) =>
        target.dispatchEvent(new CustomEvent('reRender'))
      )
    }

    // Flag the window as no longer performing AJAX updates.
    window.Simplero.state.isAjaxUpdating = false

    if (data.alert != null) {
      window.showNotice('alert', data.alert)
    }
    if (data.notice != null && !data.redirect) {
      showNoticeWithFireworks(data)
    }

    if (data.show != null) {
      for (const id of Array.from(data.show)) {
        $(idify(id)).show()
      }
    }

    // Will trigger show unless the selector condition returns some element
    // An example use case would be where you want to show the elemnet using ajax, but only if some other element does not exist/is invisible/etc
    if (data.show_unless != null) {
      for (let object of Array.from(data.show_unless)) {
        for (let condition in object) {
          const id = object[condition]
          if ($(condition).length === 0) {
            $('#' + id).show()
          }
        }
      }
    }

    if (data.hide_modal) {
      if (typeof data.hide_modal === 'boolean') {
        $('#modal-container .modal').modal?.('hide') // Bootstrap modals
        // New ViewComponent/Tailwind modals — only open ones, or closed prompt/confirm/page modals fire modal:hidden too
        $('.js-modal[aria-hidden="false"]').each((_, el) => el.modal?.('hide'))
      } else {
        const el = document.querySelector(`#${data.hide_modal}`)
        if (el) {
          if (el.modal) {
            el.modal('hide')
          } else {
            $(el).modal('hide')
          }
        }
      }
    }

    if (data.clear_modal) {
      $('#modal-container .modal input[type=text]').val('')
    }
    if (data.clear_form) {
      $(data.clear_form).find('input[type=text]').val('')
    }
    if (data.clear_field) {
      $(data.clear_field).val('')
    }

    if (data.reset_form) {
      // More standard way of doing things than clear_form above - handles more things than just text inputs (e.g tiptap wysiwyg)
      const elements =
        data.reset_form == true
          ? [eventTarget]
          : document.querySelectorAll(idify(data.reset_form))
      elements.forEach((el) => el.closest('form')?.reset())
    }

    if (data.remove_clickover) {
      $('.clickover').remove()
    }

    if (data.highlight != null) {
      for (const id of Array.from(data.highlight)) {
        const selector = idify(id)
        const $el = $(`${selector}, ${selector} td`)

        if ($el.effect) {
          $el.effect('highlight', {}, 500)
        } else {
          $el.each((_, el) => {
            highlight(el)
          })
        }
      }
    }
    each_selector(data.highlight_by_selector, function () {
      this.effect('highlight', {}, 3000)
    })

    if (data.scroll_into_view != null) {
      const scrollTarget =
        data.scroll_into_view == true
          ? eventTarget
          : document.querySelector(idify(data.scroll_into_view))

      const scroll = () => {
        scrollIntoView(scrollTarget, {
          block: data.scroll_into_view_block || 'center',
          scrollMode: 'if-needed',
          behavior: 'smooth',
        })
      }
      if (data.scroll_into_view_delay != null) {
        setTimeout(scroll, parseFloat(data.scroll_into_view_delay) || 0)
      } else {
        scroll()
      }
    }

    if (data.modal != null) {
      showModal(data.modal, { ...data.modal_options })
    }

    if (data.clickover != null && eventTarget) {
      const $anchor = $(eventTarget)
      $anchor
        .clickover({
          title: data.clickover.title,
          content: data.clickover.content,
          html: true,
          container: $anchor.data('container') || 'body',
          placement: $anchor.data('placement') || 'bottom',
        })
        .data('clickover')
        .clickery()
    }

    // When `top_frame: true` is set on a redirect/reload directive, navigate the
    // top window instead of the current frame. Used by site forms inside iframes
    // (cart, signup, embedded widgets) that need to break out of the iframe.
    const windowToUse = data.top_frame && window.top ? window.top : window

    // Leaving on purpose is not leaving by accident: stand down the "unsaved changes" warning
    // before anything below navigates, or a page whose controls save themselves
    // (`data-behavior="submit-on-change"`) gets asked "Leave site?" on the app's own redirect. Why
    // `$.active` cannot work that out for itself: see ~/common/helpers/unload_warning.
    //
    // Ahead of every branch — the reload just below, `redirect_method: 'post'`, Turbolinks — since
    // all of them are the app navigating deliberately. Turbolinks earns its place in that list
    // rather than riding along: `visit` usually swaps the body without unloading, but it falls back
    // to a real `document.location.href` for a cross-origin URL, a response it cannot use, an XHR
    // error, or a browser it does not support (turbolinks.coffee:36, 95, 106, 708) — and an
    // external `redirect` is exactly the first of those.
    //
    // clearTrixBeforeUnload() stays per-branch rather than joining it here: it destructively
    // removes DOM inputs, which would be wrong on the Turbolinks visits that do NOT unload.
    //
    // `!= null` rather than the plain truthiness the jquery-ujs block below uses, so this matches
    // the two conditions that actually navigate — `data.reload != null` and a truthy
    // `data.redirect`. Conditional, not unconditional: every submit-on-change save's own response
    // comes through here too, and suppressing those would close the window the warning exists for.
    if (data.redirect || data.reload != null) suppressUnloadWarning()

    if (data.reload != null) {
      clearTrixBeforeUnload()
      windowToUse.location.reload()
    }

    if (data.click) {
      $(data.click).click()
    }

    if (data.post_message && window.parent) {
      window.parent.postMessage(data.post_message, '*')
    }

    if (data.redirect || data.reload) {
      // We use jquery-ujs in admin. It disables form elements on submit and enables them once the request completes (even when doing ajax requests)
      // And is responsible for all the data-disable-with stuff.
      // However, for ajax requests, sometimes, we respond with a `redirect` or `reload` directive to redirect the client to another page using javascript.
      // jquery-ujs is not aware of this and will instantly re-enable the form elements after the ajax request completes, even if it responsed with a redirect
      // that we are now going to do with JS.
      // This in many instances leads to a state where things appear broken, as if nothing happened.
      // This is a hack fix around that.

      if (eventTarget && $.rails?.disableFormElements) {
        setTimeout(() => {
          $.rails.disableFormElements($(eventTarget))
        }, 0)
      }

      if (eventTarget && window.Rails?.disableElement) {
        setTimeout(() => {
          window.Rails.disableElement(eventTarget)
        }, 0)
      }
    }

    if (data.redirect) {
      if (data.redirect_method === 'post') {
        var add_form_input = function (form, name, value) {
          if (Array.isArray(value)) {
            for (let v of Array.from(value)) {
              add_form_input(form, `${name}[]`, v)
            }
          } else {
            const input = document.createElement('input')
            input.type = 'hidden'
            input.name = name
            input.value = value
            form.appendChild(input)
          }
        }

        const form = document.createElement('form')
        document.body.appendChild(form)
        form.method = 'post'
        form.action = data.redirect
        add_form_input(
          form,
          'authenticity_token',
          $('meta[name="csrf-token"]').attr('content')
        )
        for (let name in data.redirect_params) {
          add_form_input(form, name, data.redirect_params[name])
        }
        form.submit()
      } else if (data.redirect_replace) {
        // Navigate without leaving a history entry behind. For a page inside an iframe — a wizard
        // handing its frame over to an editor, say — a normal redirect means the user's next Back
        // press silently rewinds that (often hidden) frame instead of leaving the page.
        //
        // Not via Turbolinks: `visit` is `fetch`, which always ends in reflectNewUrl ->
        // history.pushState (turbolinks.coffee), and Turbolinks 3 has no replace option.
        //
        // Only for GET redirects — a redirect_method: 'post' redirect submits a generated form and
        // brings its own history behavior, so combining the two would not do what it looks like.
        clearTrixBeforeUnload()
        windowToUse.location.replace(data.redirect)
      } else {
        if (window.Turbolinks) {
          window.Turbolinks.visit(data.redirect)
        } else {
          clearTrixBeforeUnload()
          windowToUse.location.href = data.redirect
        }

        if (data.notice != null) {
          setTimeout(() => showNoticeWithFireworks(data), 1000)
        }
      }
    }

    if (data.open_in_new_tab) {
      window.open(data.open_in_new_tab, '_blank', 'noopener,noreferrer')
    }

    if (data.table_dnd_update != null) {
      for (const id of Array.from(data.table_dnd_update)) {
        $('#' + id).tableDnDUpdate()
      }
    }

    // Pass object with { id1: 'event1', id2: 'event2' }
    if (data.trigger != null) {
      for (const id in data.trigger) {
        const tevent = data.trigger[id]
        $(idify(id)).trigger(tevent)
      }
    }

    // Dispatches events using vanilla JS on the eventTarget (usually the element that initiated the ajax request)
    // You can either do dispatch_event: [event_1, event_2, event_3]
    // Or dispatch_event: { event_1: { event_1_details... }, event_2: { event_2_details... } }
    if (data.dispatch_event) {
      let events = data.dispatch_event

      // Normalize to be objects
      if (Array.isArray(events))
        events = Object.fromEntries(events.map((event) => [event, null]))

      for (const eventName in events) {
        let { to, ...eventDetail } = events[eventName] || {}

        let target = to
          ? document.querySelector(idify(to))
          : $(eventTarget).data('portal') || eventTarget

        if (!target?.isConnected) target = document
        target.dispatchEvent(
          new CustomEvent(eventName, { detail: eventDetail, bubbles: true })
        )
      }
    }

    if (data.page_title != null) {
      if (typeof data.page_title === 'string')
        data.page_title = { title: data.page_title }

      if (
        !data.page_title.if_url ||
        window.location.href.startsWith(data.page_title.if_url)
      ) {
        document.title = data.page_title.title
      }
    }

    if (data.replace_query_params != null) {
      let url = new URL(window.location.href)
      url.search = data.replace_query_params

      window.history.pushState({}, '', url)
    }

    if (data.replace_browser_url != null) {
      window.history.replaceState(
        window.history.state,
        document.title,
        data.replace_browser_url
      )
    }

    if (data.for_parent_frame && window.parent !== window) {
      const iframe = getIframeInParentDoc()
      iframe.ownerDocument.defaultView.AjaxHelper.doUpdate(
        data.for_parent_frame,
        iframe
      )
    }

    if (data.for_iframes) {
      document.querySelectorAll('iframe').forEach((iframe) => {
        try {
          console.log('updating', iframe, data.for_iframes)
          iframe.contentWindow.AjaxHelper.doUpdate(data.for_iframes, iframe)
          console.log('updated iframe', iframe)
          console.log(iframe.contentWindow.AjaxHelper)
        } catch (e) {
          // maybe a cross origin iframe
        }
      })
    }

    if (data.for_mobile_app) {
      Object.entries(data.for_mobile_app).forEach(([key, value]) => {
        if (window.Bridge && window.Bridge[key]) {
          window.Bridge[key](value)
        }
      })
    }

    if (data.ordered) {
      ;(async () => {
        for (const change of data.ordered) {
          if (typeof change === 'number') {
            await new Promise((resolve) => setTimeout(resolve, change))
          } else {
            AjaxHelper.doUpdate(change, eventTarget)
          }
        }
      })()
    }

    if (Array.isArray(data.ajax_updates)) {
      for (const update of data.ajax_updates) {
        AjaxHelper.doUpdate(update, eventTarget)
      }
    }
  },

  // Asks first: a reload would lose whatever the page hasn't saved (an autosave that just
  // failed, a half-written message). Once: several requests failing together ask once.
  reloadSignedOut(message) {
    if (AjaxHelper.askedToReloadSignedOut) return
    AjaxHelper.askedToReloadSignedOut = true
    if (
      window.confirm(
        message ||
          "You've been signed out. Sign in again? Changes on this page that aren't saved will be lost."
      )
    ) {
      // They just agreed to lose unsaved changes: no second "Leave page?" warning.
      suppressUnloadWarning()
      reloadTopmostPage()
      // Another warning (a recording in progress) can still cancel the reload: the page is
      // then still here, and the next failure must ask again.
      setTimeout(() => (AjaxHelper.askedToReloadSignedOut = false), 3000)
    } else {
      // Cancelled: the next failed request may ask again.
      AjaxHelper.askedToReloadSignedOut = false
    }
  },

  error(message) {
    const text = AjaxHelper.errorText(message)
    // Several requests failing together (a computer waking up) each land here: one alert,
    // not one more every time OK is clicked.
    if (
      text === AjaxHelper.lastErrorText &&
      Date.now() - AjaxHelper.lastErrorClosedAt < 10_000
    ) {
      return
    }
    alert(text)
    AjaxHelper.lastErrorText = text
    AjaxHelper.lastErrorClosedAt = Date.now()
  },

  errorText(message) {
    // The server's own alerts are full sentences that already apologize: wrapping them
    // read "Error: An error occurred. We're so sorry. Please try again.. We're so sorry…".
    if (message && /please try again\.?$/i.test(message)) return message
    if (window.I18n)
      return window.I18n.t('general.error_processing', { message })
    if (message) return `Error: ${message}. We're so sorry. Please try again.`
    return `An error occurred. We're so sorry. Please try again.`
  },

  successFunc(data) {
    // doUpdate assumes a payload object; null/non-objects throw (e.g. data.hide)
    // and abort the caller's success handler mid-flight.
    if (!data || typeof data !== 'object' || Array.isArray(data)) return
    AjaxHelper.doUpdate(data)
  },

  errorFunc(xhr, error, message) {
    // Signed out (an idle session expired, or a logout elsewhere): an apology here left the page
    // waiting forever, with no word that a login was needed. Reload instead: the page's own
    // login flow takes over, signing back in through the secure host when it still has a
    // session (the Coach apps' logincheck bounce), or showing the login page.
    if (xhr?.status === 401 && xhr.responseJSON?.signed_out) {
      AjaxHelper.reloadSignedOut(xhr.responseJSON.signed_out_message)
      return
    }
    // Rate limited (Rack::Attack's `alert`, or a controller's own 429 `error` such as
    // domains#check_status). Both are full sentences with the wait time, so show them as-is
    // instead of wrapping them in "Error: …. Please try again."
    if (xhr?.status === 429) {
      alert(
        xhr.responseJSON?.alert ||
          xhr.responseJSON?.error ||
          "You're making too many requests too quickly. Please wait a few minutes and try again."
      )
      return
    }
    // Prefer server-provided alert from a JSON error body (e.g. forbidden /
    // session-expired perform_action) over jQuery's generic statusText or
    // "Unexpected end of JSON input" for empty responses.
    const jsonAlert = xhr?.responseJSON?.alert || xhr?.responseJSON?.error
    if (jsonAlert) {
      AjaxHelper.error(jsonAlert)
      return
    }
    if (error === 'parsererror' && !(xhr?.responseText || '').trim()) {
      // No message — AjaxHelper.error already has the generic apology text.
      AjaxHelper.error()
      return
    }
    AjaxHelper.error(message)
  },
}

window.AjaxHelper = AjaxHelper

// Disable / re-enable submit buttons on a form while a client-side payment action (Stripe 3DS)
// is in flight. Rails UJS re-enables them on `ajax:complete`, which fires synchronously right
// after `ajax:success` — *before* our async Stripe.js promise mounts the 3DS modal. Without
// re-disabling, the customer sees the modal AND a clickable submit button and can fire a second
// form submission. We swap the label to `data-disable-with` (matching Rails UJS's own behavior)
// so the visual cue is consistent.
//
// Bookkeeping: we mark each submit we disable with `data-three-ds-disabled-by-us` so the
// re-enable side only touches submits we actually disabled — pre-disabled submits stay disabled.
function setFormSubmitsDisabled(form, disabled) {
  // `button:not([type])` is intentional: a <button> with no explicit type defaults to type=submit,
  // so those submit the form too and must be disabled alongside the explicit submits.
  const submits = form.querySelectorAll(
    'input[type=submit], button[type=submit], button:not([type])'
  )
  submits.forEach((submit) => {
    if (disabled) {
      if (submit.disabled) return
      submit.disabled = true
      submit.dataset.threeDsDisabledByUs = '1'
      const disableWith = submit.dataset.disableWith
      if (!disableWith) return
      if (submit.tagName === 'INPUT') {
        submit.dataset.threeDsOriginalLabel = submit.value
        submit.value = disableWith
      } else {
        submit.dataset.threeDsOriginalLabel = submit.textContent
        submit.textContent = disableWith
      }
    } else {
      if (submit.dataset.threeDsDisabledByUs !== '1') return
      submit.disabled = false
      delete submit.dataset.threeDsDisabledByUs
      const saved = submit.dataset.threeDsOriginalLabel
      if (!saved) return
      if (submit.tagName === 'INPUT') {
        submit.value = saved
      } else {
        submit.textContent = saved
      }
      delete submit.dataset.threeDsOriginalLabel
    }
  })
}

// When a server response asks for a client-side payment action (Stripe 3DS, or a provider
// redirect the buyer approves and returns from), run that action *before* we let the rest of
// ajax-update touch the page. handleClientSideSubmitResult either navigates to data.callback_url
// (frictionless / successful challenge) or surfaces a Stripe error string. This is the single
// hook for any one-click saved-card flow that lands in requires_action on the server. Keyed on
// attempt_3ds / redirect_url so it's a no-op for every other response — non-Stripe processors do
// not enter this branch and their submit-disable behavior is unchanged.
async function maybeHandleClientPaymentAction(form, data) {
  if (!data || !(data.attempt_3ds || data.redirect_url)) return false

  // A provider redirect needs no Stripe.js — the buyer approves there and comes back through the
  // transaction's return_url. Handled before the publishable-key requirement below, which only
  // the client_secret paths need.
  if (data.redirect_url) {
    setFormSubmitsDisabled(form, true)
    window.location.href = data.redirect_url
    return true
  }

  const publishableKey = form?.dataset?.stripePublishableKey
  if (!publishableKey) {
    AjaxHelper.error(
      'Payment authentication is required but the Stripe publishable key was not exposed on this form.'
    )
    return true
  }

  // Rails UJS re-enables the form's submit on `ajax:complete`, which fires synchronously right
  // after this handler returns — well before our Stripe.js promise resolves and shows the 3DS
  // modal. Schedule a re-disable for the next macrotask so it runs *after* Rails UJS's enable,
  // keeping the button disabled across the entire 3DS dance.
  //
  // The `threeDsPending` flag lets us cancel that scheduled disable if the Stripe flow exits
  // *before* the timeout fires — e.g. `handleClientSideSubmitResult` returns synchronously with
  // an `'Unknown error'` because the response was malformed. Without this guard the form would
  // get stuck disabled even on the synchronous-error path. Cleared on every exit (error or
  // success / nav).
  form.dataset.threeDsPending = '1'
  setTimeout(() => {
    if (form.dataset.threeDsPending === '1') setFormSubmitsDisabled(form, true)
  }, 0)

  const errorMessage = await handleClientSideSubmitResult(data, publishableKey)
  delete form.dataset.threeDsPending
  if (errorMessage) {
    AjaxHelper.error(errorMessage)
    setFormSubmitsDisabled(form, false)
  }
  // On success, handleClientSideSubmitResult navigates via location.href — submit stays disabled.
  return true
}

$(document).on(
  'ajax:success',
  '[data-behavior~=ajax-update]',
  function (event, data) {
    if (this === event.target) {
      const finalData = data || event.originalEvent?.detail?.[0]
      const form = this

      const runUpdate = () => {
        AjaxHelper.doUpdate(finalData, form)

        // Dispatch CustomEvent to allow stimulus controllers to react to the ajax update
        form.dispatchEvent(
          new CustomEvent('ajax-update:complete', {
            detail: { data: finalData },
            bubbles: true,
          })
        )
      }

      // Only Stripe 3DS responses need the async client-side payment dance. Every other
      // ajax-update response must run synchronously, exactly as before — deferring it to a
      // microtask would push `doUpdate` after Rails UJS's `ajax:complete`, breaking features
      // that hook `ajax:complete` to tear down UI (funnel inline-stats spinner, side-panel
      // loader, etc.) on the assumption the DOM was already updated.
      if (!finalData || !(finalData.attempt_3ds || finalData.redirect_url)) {
        runUpdate()
        return
      }

      maybeHandleClientPaymentAction(form, finalData)
        .then((handled) => {
          // handled is always true here (attempt_3ds or redirect_url is set): we either navigated on success
          // or surfaced a Stripe error — so we never fall through to a normal page update.
          if (!handled) runUpdate()
        })
        .catch((err) => {
          // If the client-side payment action (Stripe 3DS) throws — e.g. Stripe.js fails to load
          // or returns a malformed result — surface the failure to the user instead of leaving the
          // page in a silent broken state. ENG-29627 was caused by exactly this kind of silent
          // failure. Also make sure the submit is re-enabled (and clear the pending-disable flag
          // so any in-flight setTimeout becomes a no-op) so the customer can retry.
          console.error('ajax-update handler error:', err)
          AjaxHelper.error(err?.message || 'Something went wrong')
          delete form.dataset.threeDsPending
          setFormSubmitsDisabled(form, false)
        })
    }
  }
)

$(document).on(
  'ajax:error',
  '[data-behavior~=ajax-update]',
  function (event, xhr, error, message) {
    if (this === event.target) {
      // errorFunc reads the server's JSON alert; statusText alone is empty over HTTP/2.
      AjaxHelper.errorFunc(...ajaxErrorArgs(event, xhr, error, message))
    }
  }
)

$.sajax = function (options) {
  if (options.element) {
    const $el = $(options.element)
    $el.trigger('sajax:before')

    const ogComplete = options.complete
    options.complete = function (...args) {
      ogComplete?.(...args)
      $(options.element).trigger('sajax:complete', ...args)
    }
  }

  const callerSuccess = options.success

  options.success = function (data, status, xhr) {
    AjaxHelper.successFunc(data, status, xhr)
    if (callerSuccess) {
      callerSuccess(data, status, xhr)
    }
  }
  if (!options.error) options.error = AjaxHelper.errorFunc
  if (options.type == null) {
    options.type = 'POST'
  }
  return $.ajax(options)
}
